@if (loading()) {
    <div class="flex flex-col gap-4">
        <p-skeleton height="7rem" borderRadius="12px" />
        <div class="grid grid-cols-2 xl:grid-cols-4 gap-4">
            @for (i of [1, 2, 3, 4]; track i) {
                <p-skeleton height="7rem" borderRadius="12px" />
            }
        </div>
        <p-skeleton height="24rem" borderRadius="12px" />
    </div>
} @else if (customer(); as c) {
    <fb-page-header
        [title]="fullName()"
        [subtitle]="('CUSTOMER_SINCE' | translate) + ' ' + (c.created | date: 'mediumDate')"
        [backLink]="['/organizations', orgId(), 'crm', 'customers']"
        [backLabel]="'CUSTOMERS' | translate" />

    <!-- Identity -->
    <section class="flex flex-wrap items-center gap-6 p-5 mb-4 rounded-md bg-surface border border-border">
        <span class="size-10 shrink-0 rounded-full flex items-center justify-center bg-concern-accent text-concern-accent-title text-lg font-semibold">
            {{ initials() }}
        </span>
        <div class="flex flex-wrap gap-x-8 gap-y-3 flex-1">
            <div class="flex flex-col gap-0.5">
                <span class="text-xs font-semibold text-text-muted uppercase tracking-widest">{{ 'EMAIL' | translate }}</span>
                <span class="text-sm text-text">{{ c.email || '–' }}</span>
            </div>
            <div class="flex flex-col gap-0.5">
                <span class="text-xs font-semibold text-text-muted uppercase tracking-widest">{{ 'PHONE' | translate }}</span>
                <span class="text-sm text-text">{{ c.phone || '–' }}</span>
            </div>
            @if (c.isCompany && c.companyName) {
                <div class="flex flex-col gap-0.5">
                    <span class="text-xs font-semibold text-text-muted uppercase tracking-widest">{{ 'COMPANY_NAME' | translate }}</span>
                    <span class="text-sm text-text">{{ c.companyName }}</span>
                </div>
            }
            <div class="flex flex-col gap-0.5">
                <span class="text-xs font-semibold text-text-muted uppercase tracking-widest">{{ 'LAST_VISIT' | translate }}</span>
                <span class="text-sm text-text">{{ stats().lastVisit ? (stats().lastVisit | date: 'mediumDate') : ('NEVER' | translate) }}</span>
            </div>
        </div>
        <div class="flex flex-wrap gap-2">
            @if (c.blackListed) {
                <p-tag [value]="'BLOCKED' | translate" severity="danger" icon="ph ph-prohibit" />
            }
            @if (c.excludeCrm) {
                <p-tag [value]="'NO_MARKETING' | translate" severity="secondary" />
            }
        </div>
    </section>

    <section class="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <fb-stat-card [label]="'TOTAL_BOOKINGS' | translate" [value]="stats().bookings" icon="ph-calendar-check" />
        <fb-stat-card [label]="'LIFETIME_VALUE' | translate" [value]="utility.currencyTotals(stats().totals, 'lifetimeValue')" icon="ph-coins" />
        <fb-stat-card [label]="'AVERAGE_SPEND' | translate" [value]="utility.currencyTotals(stats().totals, 'averageValue')" icon="ph-receipt" />
        <fb-stat-card [label]="'LOCATIONS_VISITED' | translate" [value]="venues().length" icon="ph-map-pin" />
    </section>

    <p-tabs class="fb-tabs" [value]="selectedTab()" (valueChange)="selectedTab.set($any($event))">
        <p-tablist>
            <p-tab value="history"><i class="ph ph-clock-counter-clockwise mr-1.5"></i>{{ 'HISTORY' | translate }}</p-tab>
            <p-tab value="venues"><i class="ph ph-buildings mr-1.5"></i>{{ 'VENUES' | translate }} ({{ venues().length }})</p-tab>
            <p-tab value="profile"><i class="ph ph-user-circle mr-1.5"></i>{{ 'ORG_PROFILE' | translate }}</p-tab>
        </p-tablist>
        <p-tabpanels>
            <p-tabpanel value="history">
                <div class="rounded-md bg-surface border border-border overflow-hidden">
                    <fb-customer-history [items]="history()" [showVenue]="true" />
                </div>
            </p-tabpanel>

            <p-tabpanel value="venues">
                <p class="text-sm text-text-muted mb-4">{{ 'CUSTOMER_VENUES_DESC' | translate }}</p>
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    @for (venue of venues(); track venue.clientId) {
                        <div class="flex items-center gap-4 p-4 rounded-md bg-surface border border-border">
                            <span class="size-9 shrink-0 rounded-md flex items-center justify-center bg-surface-2 text-text-2 text-lg">
                                <i class="ph ph-storefront"></i>
                            </span>
                            <div class="flex flex-col flex-1 min-w-0">
                                <span class="font-semibold text-text truncate">{{ venue.name }}</span>
                                <span class="text-xs text-text-muted">
                                    {{ venue.bookings }} {{ 'BOOKINGS' | translate | lowercase }} ·
                                    {{ venue.value }} ·
                                    {{ venue.lastVisit ? (venue.lastVisit | date: 'mediumDate') : ('NEVER' | translate) }}
                                </span>
                            </div>
                            <button
                                pButton
                                type="button"
                                severity="secondary"
                                [text]="true"
                                size="small"
                                [loading]="syncing() === venue.clientId"
                                [title]="'APPLY_ORG_PROFILE' | translate"
                                (click)="confirmSync(venue)">
                                <i class="ph ph-arrows-down-up"></i>
                            </button>
                            <a pButton size="small" [outlined]="true" [routerLink]="['venues', venue.clientId]">
                                <span>{{ 'OPEN' | translate }}</span>
                                <i class="ph ph-caret-right"></i>
                            </a>
                        </div>
                    } @empty {
                        <fb-empty-state class="lg:col-span-2" icon="ph-buildings" [title]="'NO_LINKED_VENUES' | translate" />
                    }
                </div>
            </p-tabpanel>

            <p-tabpanel value="profile">
                <p class="text-sm text-text-muted mb-4 flex items-center gap-2">
                    <i class="ph ph-info"></i>
                    {{ 'ORG_PROFILE_DESC' | translate }}
                </p>
                <fb-customer-form [customer]="$any(c)" [saving]="saving()" (save)="saveProfile($event)" />
            </p-tabpanel>
        </p-tabpanels>
    </p-tabs>
} @else {
    <fb-empty-state icon="ph-user-circle-dashed" [title]="'CUSTOMER_NOT_FOUND' | translate" [text]="'CUSTOMER_NOT_FOUND_TEXT' | translate">
        <a pButton [outlined]="true" [routerLink]="['/organizations', orgId(), 'crm', 'customers']">{{ 'BACK_TO_CUSTOMERS' | translate }}</a>
    </fb-empty-state>
}
